/**
 * The Partner programme, for Caren and Daniel: /admin/partners
 *
 * Its own page rather than another card on /admin, because it answers a
 * different question. /admin is "what can this person reach", asked about one
 * account at a time. This is "who is owed what", asked about everybody at
 * once, and the month-end job of paying them.
 *
 * ── THE NUMBERS COME FROM THE SAME PLACE THE PARTNER SEES ───────────────────
 *
 * Every figure here is the same query behind /partner, with an id instead of a
 * session. That is deliberate: the failure mode of two implementations is a
 * Partner reading one total and being paid another, noticing, and being right.
 *
 * ── PAYING IS DELIBERATELY TWO STEPS ────────────────────────────────────────
 *
 * "Create payment" claims the commissions and records what is owed. "Mark
 * paid" records that the money actually left. They are separate because they
 * happen at different times — the second one is you coming back from your bank
 * — and collapsing them would mean either claiming commissions before the
 * amount is known or leaving them claimable twice.
 */

import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { BUTTON } from "@/lib/buttonStyles";
import { Loader2 } from "lucide-react";

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
/** 4000 -> "40%". Basis points everywhere below the UI; percent on screen. */
const pct = (bps: number) => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`;

/**
 * The standalone page at /admin/partners.
 *
 * Thin: everything is in PartnersPanel below, because /admin renders the same
 * thing as a tab. Two copies of this screen would drift, and the figures on it
 * are what people are paid from.
 */
export default function AdminPartners() {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) setLocation("/login?returnTo=%2Fadmin%2Fpartners");
    else if (user.role !== "admin") setLocation("/");
  }, [loading, user, setLocation]);

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="Admin" showPrimaryAction={false} />
      <main className="flex-1">
        <div className="container max-w-6xl py-8 sm:py-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Partners</p>
          <p className="mt-3 text-base text-muted-foreground">
            <Link href="/admin" className="underline hover:text-foreground">
              Back to accounts
            </Link>
          </p>
          <PartnersPanel />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/** Everything on the Partners screen. Rendered standalone above, and as a tab on /admin. */
export function PartnersPanel() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const partners = trpc.admin.partners.useQuery();

  const rows = partners.data ?? [];
  /* Summed from the same per-Partner figures the table below prints, so the
     tiles and the rows can never disagree — they are literally the same
     numbers added up in the browser rather than a second query. */
  const owed = rows.reduce((s, p) => s + p.payable, 0);
  const settling = rows.reduce((s, p) => s + p.pending, 0);

  return (
    <>
      <div>
          {/* The two numbers that answer "what do we owe right now", across
              everybody. The other two a Partner sees — sent, and earned in
              total — are per-Partner history rather than anything to act on,
              so they stay on the individual panel. */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2 max-w-2xl">
            <Tile label="Ready to pay" value={money(owed)} />
            <Tile label="Still settling" value={money(settling)} />
          </div>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-2xl">
            Across {rows.length} {rows.length === 1 ? "Partner" : "Partners"}. Anything earned in
            the last 30 days is still settling and is not ready to pay yet.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" className={BUTTON.primarySm} onClick={() => setCreating(v => !v)}>
              {creating ? "Cancel" : "Add a Partner"}
            </button>
          </div>

          {creating && (
            <NewPartner
              partners={rows}
              onDone={() => {
                setCreating(false);
                partners.refetch();
              }}
            />
          )}

          {partners.isLoading ? (
            <p className="mt-10 text-base text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="mt-10 text-base text-muted-foreground">No Partners yet.</p>
          ) : (
            <div className="mt-8 overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-base">
                <thead>
                  <tr className="bg-muted text-left">
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Status</Th>
                    <Th right>Rate</Th>
                    <Th right>People</Th>
                    <Th right>Earned</Th>
                    <Th right>Settling</Th>
                    <Th right>Ready</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(p => (
                    <tr key={p.id} className="border-t border-border">
                      <Td className="font-mono text-sm">{p.code}</Td>
                      <Td>
                        {p.name}
                        {p.parentCode ? (
                          <span className="block text-sm text-muted-foreground">
                            introduced by {p.parentCode}
                          </span>
                        ) : null}
                      </Td>
                      <Td>
                        <span
                          className={
                            p.status === "active"
                              ? "font-semibold text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {p.status}
                        </span>
                      </Td>
                      <Td right className="tabular-nums">
                        {pct(p.commissionBps)}
                        {p.tier2Bps > 0 ? (
                          <span className="block text-sm text-muted-foreground">
                            +{pct(p.tier2Bps)} tier 2
                          </span>
                        ) : null}
                      </Td>
                      <Td right className="tabular-nums">
                        {p.referrals}
                      </Td>
                      <Td right className="tabular-nums">
                        {money(p.earned)}
                      </Td>
                      <Td right className="tabular-nums text-muted-foreground">
                        {money(p.pending)}
                      </Td>
                      <Td right className="tabular-nums font-semibold">
                        {money(p.payable)}
                      </Td>
                      <Td right>
                        <button
                          type="button"
                          className={BUTTON.secondarySm}
                          onClick={() => setOpenId(openId === p.id ? null : p.id)}
                        >
                          {openId === p.id ? "Close" : "Open"}
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {openId !== null && (
            <PartnerDetail partnerId={openId} onChanged={() => partners.refetch()} />
          )}

      </div>
    </>
  );
}

/* ── Adding a Partner ────────────────────────────────────────────────────── */

function NewPartner({
  partners,
  onDone,
}: {
  partners: Array<{ id: number; code: string; name: string }>;
  onDone: () => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rate, setRate] = useState("40");
  const [tier2, setTier2] = useState("0");
  const [parent, setParent] = useState("");
  const [error, setError] = useState("");

  const create = trpc.admin.createPartner.useMutation();

  return (
    <form
      className="mt-6 rounded-lg border border-border bg-card p-6 grid gap-4 max-w-2xl"
      onSubmit={async e => {
        e.preventDefault();
        setError("");
        try {
          await create.mutateAsync({
            code,
            name,
            email,
            userId: null,
            /* Percent on screen, basis points on the wire. Rounded so "12.5"
               becomes 1250 rather than a fraction of a basis point. */
            commissionBps: Math.round(parseFloat(rate || "0") * 100),
            tier2Bps: Math.round(parseFloat(tier2 || "0") * 100),
            parentPartnerId: parent ? Number(parent) : null,
            status: "active",
            notes: null,
          });
          onDone();
        } catch (err) {
          setError(err instanceof Error ? err.message : "That did not work.");
        }
      }}
    >
      <h2 className="font-['Fraunces'] text-xl font-semibold text-foreground">New Partner</h2>

      <Field label="Code" hint="Goes in their links. Letters, numbers, hyphens.">
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          required
          maxLength={32}
          className="w-full rounded-md border border-border bg-background px-3 py-3 font-mono"
        />
      </Field>
      <Field label="Name">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          required
          className="w-full rounded-md border border-border bg-background px-3 py-3"
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          className="w-full rounded-md border border-border bg-background px-3 py-3"
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Commission %">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={rate}
            onChange={e => setRate(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-3 tabular-nums"
          />
        </Field>
        <Field label="Second tier %" labelNote="0 for most Partners">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={tier2}
            onChange={e => setTier2(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-3 tabular-nums"
          />
        </Field>
      </div>

      <Field label="Introduced by" hint="Who recruited them, if anybody. That Partner earns their second tier on this one's sales.">
        <select
          value={parent}
          onChange={e => setParent(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-3"
        >
          <option value="">Nobody</option>
          {partners.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
      </Field>

      {error && <p className="text-base font-semibold text-destructive">{error}</p>}

      <div>
        <button type="submit" disabled={create.isPending} className={BUTTON.primarySm}>
          {create.isPending ? "Creating…" : "Create Partner"}
        </button>
      </div>
    </form>
  );
}

/* ── One Partner ─────────────────────────────────────────────────────────── */

function PartnerDetail({ partnerId, onChanged }: { partnerId: number; onChanged: () => void }) {
  const detail = trpc.admin.partnerDetail.useQuery({ partnerId });
  const list = trpc.admin.partners.useQuery();
  const allPartners = (list.data ?? []).filter(p => p.id !== partnerId);
  const createPayout = trpc.admin.createPayout.useMutation();
  const markPaid = trpc.admin.markPayoutPaid.useMutation();
  const [error, setError] = useState("");

  if (detail.isLoading) {
    return <p className="mt-8 text-base text-muted-foreground">Loading…</p>;
  }
  const d = detail.data;
  if (!d) return null;

  const refresh = () => {
    detail.refetch();
    onChanged();
  };

  return (
    <section className="mt-10 rounded-lg border border-border bg-card p-6 sm:p-8">
      <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">
        {d.partner.name}{" "}
        <span className="font-mono text-base font-normal text-muted-foreground">
          {d.partner.code}
        </span>
      </h2>
      <p className="mt-1 text-base text-muted-foreground break-all">{d.partner.email}</p>

      <EditPartner partner={d.partner} allPartners={allPartners} onSaved={refresh} />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Ready to pay" value={money(d.totals.payable)} />
        <Tile label="Still settling" value={money(d.totals.pending)} />
        <Tile label="Sent" value={money(d.totals.paid)} />
        <Tile label="Earned in total" value={money(d.totals.earned)} />
      </div>

      {/* ── Pay them ─────────────────────────────────────────────────── */}
      <div className="mt-8 rounded-lg border border-border bg-background p-5">
        <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground">Pay this Partner</h3>
        {d.draft.amount <= 0 ? (
          <p className="mt-2 text-base text-muted-foreground">
            Nothing is payable right now. Commissions become payable 30 days after the sale.
          </p>
        ) : (
          <>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed">
              {money(d.draft.amount)} across {d.draft.commissionIds.length}{" "}
              {d.draft.commissionIds.length === 1 ? "commission" : "commissions"}. Creating the
              payment records what is owed; mark it paid once the money has actually gone.
            </p>
            <button
              type="button"
              disabled={createPayout.isPending}
              className={`${BUTTON.primarySm} mt-4`}
              onClick={async () => {
                setError("");
                try {
                  await createPayout.mutateAsync({
                    partnerId,
                    expectedAmount: d.draft.amount,
                    reference: null,
                    note: null,
                  });
                  refresh();
                } catch (err) {
                  setError(err instanceof Error ? err.message : "That did not work.");
                }
              }}
            >
              {createPayout.isPending ? "Working…" : `Create payment for ${money(d.draft.amount)}`}
            </button>
          </>
        )}
        {error && <p className="mt-3 text-base font-semibold text-destructive">{error}</p>}
      </div>

      {d.payouts.length > 0 && (
        <div className="mt-8">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground">Payments</h3>
          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-base">
              <thead>
                <tr className="bg-muted text-left">
                  <Th>Created</Th>
                  <Th right>Amount</Th>
                  <Th>Status</Th>
                  <Th>Reference</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {d.payouts.map(p => (
                  <tr key={p.id} className="border-t border-border">
                    <Td className="whitespace-nowrap">{day(p.createdAt)}</Td>
                    <Td right className="tabular-nums font-semibold">
                      {money(p.amount)}
                    </Td>
                    <Td>{p.status === "paid" ? `Paid ${p.paidAt ? day(p.paidAt) : ""}` : "Pending"}</Td>
                    <Td className="text-muted-foreground">{p.reference || "—"}</Td>
                    <Td right>
                      {p.status === "pending" && (
                        <button
                          type="button"
                          className={BUTTON.secondarySm}
                          onClick={async () => {
                            const reference = window.prompt(
                              "Reference for this payment (bank reference, PayPal id, or leave blank)"
                            );
                            if (reference === null) return;
                            await markPaid.mutateAsync({
                              payoutId: p.id,
                              reference: reference || null,
                            });
                            refresh();
                          }}
                        >
                          Mark paid
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground">
          Their people ({d.counts.total})
        </h3>
        <p className="mt-1 text-base text-muted-foreground">
          {d.counts.members} on Smart Stack +, {d.counts.buyers} own an app, {d.counts.free} free.
        </p>
      </div>

      {/* Every commission, as it stands right now.
          This is the answer to "why is that number what it is" — the tiles
          above are sums of exactly these rows, so a total that looks wrong
          can be read down to the sale that caused it without exporting
          anything. */}
      <div className="mt-8">
        <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground">
          Every commission ({d.commissions.length})
        </h3>
        {d.commissions.length === 0 ? (
          <p className="mt-1 text-base text-muted-foreground">
            Nothing yet. Rows appear here the moment a sale goes through.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-base">
              <thead>
                <tr className="bg-muted text-left">
                  <Th>Date</Th>
                  <Th>What</Th>
                  <Th right>Sale</Th>
                  <Th right>Rate</Th>
                  <Th right>Commission</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {d.commissions.map(c => (
                  <tr key={c.id} className="border-t border-border">
                    <Td className="whitespace-nowrap">{day(c.earnedAt)}</Td>
                    <Td>
                      {c.what}
                      {c.tier === 2 && c.via ? (
                        <span className="block text-sm text-muted-foreground">
                          Second tier &mdash; sold by {c.via}
                        </span>
                      ) : null}
                    </Td>
                    <Td right className="tabular-nums">
                      {money(c.baseAmount)}
                    </Td>
                    <Td right className="tabular-nums text-muted-foreground">
                      {pct(c.rateBps)}
                    </Td>
                    <Td right className="tabular-nums font-semibold">
                      {money(c.amount)}
                    </Td>
                    <Td>
                      {c.status === "reversed" ? (
                        <span className="text-muted-foreground">Refunded</span>
                      ) : c.status === "paid" ? (
                        <span className="text-muted-foreground">Paid</span>
                      ) : c.pending ? (
                        <span className="text-muted-foreground">Settling</span>
                      ) : (
                        <span className="font-semibold text-foreground">Ready</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Small shared pieces ─────────────────────────────────────────────────── */

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <div className="mt-2 font-['Fraunces'] text-2xl font-semibold text-foreground tabular-nums">
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  /** Sits under the input. For anything the person needs AFTER typing. */
  hint,
  /**
   * Sits beside the label. For anything they need BEFORE typing — "0 for most
   * Partners" is an instruction about what to enter, and under the box it was
   * being read after the decision it was meant to inform.
   */
  labelNote,
  children,
}: {
  label: string;
  hint?: string;
  labelNote?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-2">
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        {labelNote && (
          <span className="text-sm font-normal normal-case tracking-normal text-muted-foreground">
            {labelNote}
          </span>
        )}
      </span>
      {children}
      {hint && <span className="text-sm text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap ${
        right ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  right,
  className = "",
}: {
  children?: React.ReactNode;
  right?: boolean;
  className?: string;
}) {
  return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>;
}

/**
 * Change a Partner's terms.
 *
 * The rates, their status, and who recruited them. There was no way to do any
 * of this — `admin.updatePartner` existed and nothing called it, so a rate set
 * wrongly at creation could only be fixed in the database.
 *
 * ── A RATE CHANGE IS NOT RETROSPECTIVE ──────────────────────────────────────
 *
 * Every row already in the ledger carries the rate it was earned at, so
 * raising somebody to 50% today does not reprice last quarter. That is worth
 * knowing before you change it, so the form says so rather than leaving it to
 * be discovered.
 */
function EditPartner({
  partner,
  allPartners,
  onSaved,
}: {
  partner: {
    id: number;
    commissionBps: number;
    tier2Bps: number;
    status: "pending" | "active" | "suspended";
    parentPartnerId: number | null;
  };
  allPartners: Array<{ id: number; code: string; name: string }>;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState((partner.commissionBps / 100).toString());
  const [tier2, setTier2] = useState((partner.tier2Bps / 100).toString());
  const [status, setStatus] = useState(partner.status);
  const [parent, setParent] = useState(partner.parentPartnerId ? String(partner.parentPartnerId) : "");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const update = trpc.admin.updatePartner.useMutation();

  if (!open) {
    return (
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" className={BUTTON.secondarySm} onClick={() => setOpen(true)}>
          Edit commission &amp; terms
        </button>
        {saved && <span className="text-base text-muted-foreground">Saved.</span>}
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-lg border border-border bg-background p-5 grid gap-4 max-w-2xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Commission %">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={rate}
            onChange={e => setRate(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-3 py-3 tabular-nums"
          />
        </Field>
        <Field label="Second tier %" labelNote="0 for most Partners">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={tier2}
            onChange={e => setTier2(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-3 py-3 tabular-nums"
          />
        </Field>
      </div>

      <Field label="Status" labelNote="suspended earns nothing from then on">
        <select
          value={status}
          onChange={e => setStatus(e.target.value as typeof status)}
          className="w-full rounded-md border border-border bg-card px-3 py-3"
        >
          <option value="active">active</option>
          <option value="pending">pending</option>
          <option value="suspended">suspended</option>
        </select>
      </Field>

      <Field label="Introduced by" labelNote="who earns the second tier on their sales">
        <select
          value={parent}
          onChange={e => setParent(e.target.value)}
          className="w-full rounded-md border border-border bg-card px-3 py-3"
        >
          <option value="">Nobody</option>
          {allPartners.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
      </Field>

      <p className="text-sm text-muted-foreground leading-relaxed">
        A rate change applies from now on. Commissions already earned keep the rate they
        were earned at, so nothing on a statement you have already sent will move.
      </p>

      {error && <p className="text-base font-semibold text-destructive">{error}</p>}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={update.isPending}
          className={BUTTON.primarySm}
          onClick={async () => {
            setError("");
            try {
              await update.mutateAsync({
                partnerId: partner.id,
                /* Percent on screen, basis points on the wire. Rounded so
                   "12.5" becomes 1250 rather than a fraction of a point. */
                commissionBps: Math.round(parseFloat(rate || "0") * 100),
                tier2Bps: Math.round(parseFloat(tier2 || "0") * 100),
                status,
                parentPartnerId: parent ? Number(parent) : null,
              });
              setSaved(true);
              setOpen(false);
              onSaved();
            } catch (err) {
              setError(err instanceof Error ? err.message : "That did not work.");
            }
          }}
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
        <button type="button" className={BUTTON.ghost} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
